import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import './Registreren.css';
import './Mijn.css';

// Portaal voor medewerkers: /mijn (inloggen + rooster + gegevens),
// /mijn/link/:token (account maken of nieuw wachtwoord), /mijn/wachtwoord-vergeten.

async function api(url, opts = {}) {
  const r = await fetch(`/api/mw${url}`, {
    credentials: 'same-origin',
    ...opts,
    headers: { ...(opts.body ? { 'Content-Type': 'application/json' } : {}), ...(opts.headers || {}) },
  });
  const d = await r.json().catch(() => ({}));
  return { ok: r.ok, status: r.status, data: d };
}

const DAGEN = ['Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag', 'Zondag'];
const MAANDEN = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
const d0 = (s) => new Date(`${s}T12:00:00Z`);
const ymd = (d) => d.toISOString().slice(0, 10);
const plus = (s, n) => { const d = d0(s); d.setUTCDate(d.getUTCDate() + n); return ymd(d); };
const kort = (s) => { const d = d0(s); return `${d.getUTCDate()} ${MAANDEN[d.getUTCMonth()]}`; };
const nlDatum = (s) => { if (!s) return '—'; const [y, m, d] = s.split('-'); return `${d}-${m}-${y}`; };
function weekNr(s) {
  const d = d0(s);
  const day = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - day + 3);
  const first = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  return 1 + Math.round(((d - first) / 86400000 - 3 + ((first.getUTCDay() + 6) % 7)) / 7);
}

function Shell({ children, onLogout }) {
  return (
    <div className="rg-page mj-page">
      <header className="rg-top mj-top">
        <img src="/assets/logo.png" alt="Özel Uitzendbureau" />
        {onLogout && <button type="button" className="mj-logout" onClick={onLogout}>Uitloggen</button>}
      </header>
      <main className="rg-main">{children}</main>
    </div>
  );
}

function Field({ label, ...rest }) {
  return (
    <label className="rg-field">
      <span>{label}</span>
      <input {...rest} />
    </label>
  );
}

// ── Inloggen ─────────────────────────────────────────────────────────────────
function Login({ onIn }) {
  const [u, setU] = useState('');
  const [p, setP] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    const r = await api('/login', { method: 'POST', body: JSON.stringify({ gebruikersnaam: u, wachtwoord: p }) });
    setBusy(false);
    if (r.ok) onIn(); else setErr(r.data.error || 'Inloggen mislukt.');
  };
  return (
    <Shell>
      <form className="rg-section mj-narrow" onSubmit={submit}>
        <h1 className="mj-h1">Inloggen</h1>
        <p className="rg-muted mj-sub">Bekijk je rooster en je gegevens.</p>
        <div className="mj-stack">
          <Field label="Gebruikersnaam of telefoonnummer" value={u} onChange={(e) => setU(e.target.value)} autoComplete="username" autoCapitalize="none" required />
          <Field label="Wachtwoord" type="password" value={p} onChange={(e) => setP(e.target.value)} autoComplete="current-password" required />
        </div>
        {err && <p className="rg-error" role="alert">{err}</p>}
        <button className="rg-submit" disabled={busy}>{busy ? 'Bezig…' : 'Inloggen'}</button>
        <Link className="mj-link" to="/mijn/wachtwoord-vergeten">Wachtwoord vergeten?</Link>
      </form>
    </Shell>
  );
}

// ── Wachtwoord vergeten ──────────────────────────────────────────────────────
export function MijnVergeten() {
  const [u, setU] = useState('');
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    await api('/wachtwoord-vergeten', { method: 'POST', body: JSON.stringify({ gebruikersnaam: u }) });
    setSent(true);
  };
  return (
    <Shell>
      <form className="rg-section mj-narrow" onSubmit={submit}>
        <h1 className="mj-h1">Wachtwoord vergeten</h1>
        {sent ? (
          <p className="mj-sub">Als we een account met een e-mailadres bij deze gebruikersnaam hebben, staat er nu een e-mail met een link voor een nieuw wachtwoord. Kijk ook in je spam. Geen e-mail gekregen? Bel of app Özel.</p>
        ) : (
          <>
            <p className="rg-muted mj-sub">We sturen een link naar het e-mailadres dat bij Özel bekend is.</p>
            <Field label="Gebruikersnaam of telefoonnummer" value={u} onChange={(e) => setU(e.target.value)} autoComplete="username" autoCapitalize="none" required />
            <button className="rg-submit">Link sturen</button>
          </>
        )}
        <Link className="mj-link" to="/mijn">Terug naar inloggen</Link>
      </form>
    </Shell>
  );
}

// ── Account maken / nieuw wachtwoord via link ────────────────────────────────
export function MijnLink() {
  const { token } = useParams();
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [u, setU] = useState('');
  const [p, setP] = useState('');
  const [p2, setP2] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api(`/link/${encodeURIComponent(token)}`).then((r) => setInfo(r.data?.status ? r.data : { status: 'ongeldig' }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (p.length < 8) { setErr('Kies een wachtwoord van minstens 8 tekens.'); return; }
    if (p !== p2) { setErr('De twee wachtwoorden zijn niet hetzelfde.'); return; }
    setBusy(true);
    const r = await api(`/link/${encodeURIComponent(token)}`, { method: 'POST', body: JSON.stringify({ wachtwoord: p, gebruikersnaam: u }) });
    setBusy(false);
    if (r.ok) nav('/mijn', { replace: true }); else setErr(r.data.error || 'Er ging iets mis.');
  };

  if (!info) return <Shell><p className="rg-muted">Laden…</p></Shell>;
  if (info.status !== 'open') {
    return (
      <Shell>
        <div className="rg-done">
          <h1>{info.status === 'heeft-account' ? 'Je hebt al een account' : 'Link werkt niet meer'}</h1>
          <p>{info.status === 'heeft-account' ? 'Log in met je gebruikersnaam en wachtwoord.' : 'Deze link is verlopen of al gebruikt. Vraag Özel om een nieuwe.'}</p>
          <Link className="rg-submit mj-btnlink" to="/mijn">Naar inloggen</Link>
        </div>
      </Shell>
    );
  }
  const reset = info.soort === 'reset';
  return (
    <Shell>
      <form className="rg-section mj-narrow" onSubmit={submit}>
        <h1 className="mj-h1">{reset ? 'Nieuw wachtwoord' : `Welkom, ${info.voornaam}!`}</h1>
        <p className="rg-muted mj-sub">{reset ? 'Kies een nieuw wachtwoord.' : 'Maak je account aan. Daarna zie je je rooster en je gegevens.'}</p>
        <div className="mj-stack">
          {info.zelfKiezen ? (
            <Field label="Kies een gebruikersnaam" value={u} onChange={(e) => setU(e.target.value.toLowerCase())}
              autoComplete="username" autoCapitalize="none" placeholder="bijv. jan.devries" required />
          ) : (
            <div className="mj-fixed"><span>Gebruikersnaam</span><strong>{info.gebruikersnaam}</strong>
              {!reset && <small>Dit is je telefoonnummer. Hiermee log je straks in.</small>}</div>
          )}
          <Field label="Wachtwoord (minstens 8 tekens)" type="password" value={p} onChange={(e) => setP(e.target.value)} autoComplete="new-password" required />
          <Field label="Wachtwoord nog een keer" type="password" value={p2} onChange={(e) => setP2(e.target.value)} autoComplete="new-password" required />
        </div>
        {err && <p className="rg-error" role="alert">{err}</p>}
        <button className="rg-submit" disabled={busy}>{busy ? 'Bezig…' : reset ? 'Wachtwoord opslaan' : 'Account aanmaken'}</button>
      </form>
    </Shell>
  );
}

// ── Ingelogd: rooster + gegevens ─────────────────────────────────────────────
function Rooster({ data, reload }) {
  const { vandaag, van, diensten, meldingen } = data;
  const [open, setOpen] = useState(null); // datum waarvoor het opmerkingenveld open staat
  const [tekst, setTekst] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const huidigeMaandag = plus(van, 14);
  const [week, setWeek] = useState(2); // 0..3 → 2 = huidige week

  const maandag = plus(van, week * 7);
  const dagen = useMemo(() => Array.from({ length: 7 }, (_, i) => plus(maandag, i)), [maandag]);
  const perDag = useMemo(() => {
    const m = {};
    for (const x of diensten) (m[x.datum] ||= []).push(x);
    return m;
  }, [diensten]);
  const opmerkingen = useMemo(() => {
    const m = {};
    for (const x of meldingen) if (x.datum) (m[x.datum] ||= []).push(x);
    return m;
  }, [meldingen]);

  const stuur = async (datum) => {
    if (!tekst.trim()) return;
    setBusy(true); setErr('');
    const r = await api('/meldingen', { method: 'POST', body: JSON.stringify({ datum, tekst }) });
    setBusy(false);
    if (!r.ok) { setErr(r.data.error || 'Versturen mislukt.'); return; }
    setTekst(''); setOpen(null); reload();
  };

  const label = ['2 weken terug', 'Vorige week', 'Deze week', 'Volgende week'][week];
  return (
    <>
      <div className="mj-weekbar">
        <button type="button" onClick={() => setWeek((w) => Math.max(0, w - 1))} disabled={week === 0} aria-label="Vorige week">‹</button>
        <div><strong>Week {weekNr(maandag)}</strong><small>{label} · {kort(maandag)} – {kort(plus(maandag, 6))}</small></div>
        <button type="button" onClick={() => setWeek((w) => Math.min(3, w + 1))} disabled={week === 3} aria-label="Volgende week">›</button>
      </div>

      <ul className="mj-days">
        {dagen.map((d, i) => {
          const items = perDag[d] || [];
          const ops = opmerkingen[d] || [];
          const toekomst = d > vandaag;
          return (
            <li key={d} className={`mj-day ${d === vandaag ? 'is-today' : ''} ${d < vandaag ? 'is-past' : ''}`}>
              <div className="mj-day-head">
                <span className="mj-day-name">{DAGEN[i]}</span>
                <span className="mj-day-date">{kort(d)}</span>
              </div>
              {items.length ? items.map((x, k) => (
                <div key={k} className="mj-shift">
                  <strong>{x.van && x.tot ? `${x.van} – ${x.tot}` : 'Hele dag'}</strong>
                  <span>{x.klant}{x.plaats ? `, ${x.plaats}` : ''}</span>
                </div>
              )) : <p className="mj-free">Vrij</p>}

              {ops.map((o) => (
                <p key={o.id} className="mj-note"><b>Jouw opmerking:</b> {o.tekst}<small>{o.gelezen ? ' · gelezen door Özel' : ' · verstuurd'}</small></p>
              ))}

              {toekomst && (open === d ? (
                <div className="mj-compose">
                  <textarea value={tekst} onChange={(e) => setTekst(e.target.value)} maxLength={1000} rows={3}
                    placeholder="Bijv. ik kan deze dag pas om 9 uur beginnen." autoFocus />
                  {err && <p className="rg-error">{err}</p>}
                  <div className="mj-compose-actions">
                    <button type="button" className="mj-ghost" onClick={() => { setOpen(null); setTekst(''); setErr(''); }}>Annuleren</button>
                    <button type="button" className="mj-primary" disabled={busy || !tekst.trim()} onClick={() => stuur(d)}>{busy ? 'Versturen…' : 'Versturen naar Özel'}</button>
                  </div>
                </div>
              ) : (
                <button type="button" className="mj-addnote" onClick={() => { setOpen(d); setTekst(''); setErr(''); }}>+ Opmerking bij deze dag</button>
              ))}
            </li>
          );
        })}
      </ul>
      {week !== 2 && <button type="button" className="mj-link mj-center" onClick={() => setWeek(2)}>Naar deze week ({weekNr(huidigeMaandag)})</button>}
    </>
  );
}

function Melding({ meldingen, reload }) {
  const [tekst, setTekst] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const algemeen = meldingen.filter((m) => !m.datum);
  const stuur = async (e) => {
    e.preventDefault();
    setBusy(true); setMsg('');
    const r = await api('/meldingen', { method: 'POST', body: JSON.stringify({ tekst }) });
    setBusy(false);
    if (!r.ok) { setMsg(r.data.error || 'Versturen mislukt.'); return; }
    setTekst(''); setMsg('Verstuurd. Özel krijgt je bericht meteen te zien.'); reload();
  };
  return (
    <form className="rg-section" onSubmit={stuur}>
      <h2 className="mj-h2">Bericht aan Özel</h2>
      <p className="rg-muted mj-sub">Iets doorgeven over je rooster of je gegevens? Kloppen je gegevens niet? Laat het ons hier weten.</p>
      <textarea className="mj-textarea" value={tekst} onChange={(e) => setTekst(e.target.value)} maxLength={1000} rows={4} placeholder="Typ je bericht…" />
      {msg && <p className={msg.startsWith('Verstuurd') ? 'mj-ok' : 'rg-error'}>{msg}</p>}
      <button className="rg-submit" disabled={busy || !tekst.trim()}>{busy ? 'Versturen…' : 'Versturen'}</button>
      {algemeen.length > 0 && (
        <ul className="mj-sent">
          {algemeen.map((m) => (
            <li key={m.id}><span>{m.tekst}</span><small>{new Date(m.verstuurd).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' })}{m.gelezen ? ' · gelezen' : ''}</small></li>
          ))}
        </ul>
      )}
    </form>
  );
}

function Gegevens({ ik }) {
  const g = ik.gegevens;
  const rows = [
    ['Voornaam', g.voornaam], ['Achternaam', g.achternaam], ['Geboortedatum', nlDatum(g.geboortedatum)],
    ['Telefoon', g.telefoon], ['E-mail', g.email],
    ['Adres', g.adres], ['Postcode en plaats', [g.postcode, g.plaats].filter(Boolean).join(' ')],
    ['Bedrijfsnaam', g.bedrijfsnaam], ['KvK-nummer', g.kvk], ['Btw-nummer', g.btw],
  ];
  const vandaag = new Date().toISOString().slice(0, 10);
  return (
    <>
      <section className="rg-section">
        <h2 className="mj-h2">Mijn gegevens</h2>
        <p className="rg-muted mj-sub">Klopt er iets niet? Stuur ons een bericht bij Rooster. Je kunt je gegevens niet zelf aanpassen.</p>
        <dl className="mj-dl">
          <div><dt>Gebruikersnaam</dt><dd>{ik.gebruikersnaam}</dd></div>
          {rows.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v || '—'}</dd></div>)}
        </dl>
      </section>
      <section className="rg-section">
        <h2 className="mj-h2">Certificaten</h2>
        {ik.certificaten.length ? (
          <ul className="mj-list">
            {ik.certificaten.map((c, i) => {
              const verlopen = c.geldigTot && c.geldigTot < vandaag;
              return (
                <li key={i}>
                  <div><strong>{c.naam}</strong><small className={verlopen ? 'mj-red' : ''}>{c.geldigTot ? `${verlopen ? 'Verlopen op' : 'Geldig tot'} ${nlDatum(c.geldigTot)}` : 'Geen einddatum'}</small></div>
                  {c.bestandId && <a href={`/api/mw/bestand/${c.bestandId}`}>Bekijken</a>}
                </li>
              );
            })}
          </ul>
        ) : <p className="rg-muted">Nog geen certificaten bekend.</p>}
      </section>
      {ik.documenten.length > 0 && (
        <section className="rg-section">
          <h2 className="mj-h2">Documenten</h2>
          <ul className="mj-list">
            {ik.documenten.map((f) => (
              <li key={f.id}>
                <div><strong>{f.soort}</strong><small>{new Date(f.datum).toLocaleDateString('nl-NL')}</small></div>
                <a href={`/api/mw/bestand/${f.id}`}>Bekijken</a>
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}

export default function Mijn() {
  const [state, setState] = useState('laden'); // laden | uit | in
  const [ik, setIk] = useState(null);
  const [rooster, setRooster] = useState(null);
  const [tab, setTab] = useState('rooster');

  const load = useCallback(async () => {
    const [a, b] = await Promise.all([api('/ik'), api('/rooster')]);
    if (a.status === 401 || b.status === 401) { setState('uit'); return; }
    if (a.ok && b.ok) { setIk(a.data); setRooster(b.data); setState('in'); }
    else setState('fout');
  }, []);
  useEffect(() => { load(); }, [load]);

  const logout = async () => { await api('/logout', { method: 'POST' }); setIk(null); setState('uit'); };

  if (state === 'laden') return <Shell><p className="rg-muted">Laden…</p></Shell>;
  if (state === 'uit') return <Login onIn={load} />;
  if (state === 'fout') return <Shell><div className="rg-done"><h1>Er ging iets mis</h1><p>Probeer de pagina opnieuw te laden.</p></div></Shell>;

  return (
    <Shell onLogout={logout}>
      <div className="mj-hello">
        <h1 className="mj-h1">Hallo {ik.gegevens.voornaam}</h1>
      </div>
      <div className="mj-tabs" role="tablist">
        <button type="button" role="tab" aria-selected={tab === 'rooster'} className={tab === 'rooster' ? 'on' : ''} onClick={() => setTab('rooster')}>Rooster</button>
        <button type="button" role="tab" aria-selected={tab === 'gegevens'} className={tab === 'gegevens' ? 'on' : ''} onClick={() => setTab('gegevens')}>Mijn gegevens</button>
      </div>
      {tab === 'rooster' ? (
        <>
          <Rooster data={rooster} reload={load} />
          <Melding meldingen={rooster.meldingen} reload={load} />
        </>
      ) : <Gegevens ik={ik} />}
    </Shell>
  );
}
